import type { AppConfig } from "../../types/appConfig";
import { assertResponseOk, expectStatuses, fetchZekay } from "../zekay/httpClient";
import type {
  Event,
  EventParticipant,
  CreateEventRequest,
  UpdateEventRequest,
  RegisterParticipantRequest,
} from "./Event.typed";

export class EventModel {
  zekayAPI: string;

  constructor(config: AppConfig) {
    this.zekayAPI = config.zekayAPI;
  }

  async getEvents(): Promise<Event[]> {
    const r = await fetchZekay(this.zekayAPI + "/events");
    await expectStatuses(r, 200, 204);
    if (r.status === 204) return [];
    return (await r.json()) as Event[];
  }

  async getEvent(id: number): Promise<Event> {
    const r = await fetchZekay(this.zekayAPI + "/events/" + id);
    await assertResponseOk(r);
    return (await r.json()) as Event;
  }

  async createEvent(event: CreateEventRequest): Promise<Event> {
    const r = await fetchZekay(this.zekayAPI + "/events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event),
    });
    await expectStatuses(r, 201);
    return (await r.json()) as Event;
  }

  async updateEvent(event: UpdateEventRequest): Promise<Event> {
    const r = await fetchZekay(this.zekayAPI + "/events/" + event.id, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event),
    });
    await assertResponseOk(r);
    return (await r.json()) as Event;
  }

  async deleteEvent(id: number): Promise<void> {
    const r = await fetchZekay(this.zekayAPI + "/events/" + id, { method: "DELETE" });
    await expectStatuses(r, 204);
  }

  async getEventParticipants(eventId: number): Promise<EventParticipant[]> {
    const r = await fetchZekay(this.zekayAPI + "/events/" + eventId + "/participants");
    await expectStatuses(r, 200, 204);
    if (r.status === 204) return [];
    return (await r.json()) as EventParticipant[];
  }

  async registerForEvent(participant: RegisterParticipantRequest): Promise<EventParticipant> {
    const r = await fetchZekay(this.zekayAPI + "/events/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(participant),
    });
    await expectStatuses(r, 201);
    return (await r.json()) as EventParticipant;
  }

  async deleteEventParticipant(eventId: number, participantId: number): Promise<void> {
    const r = await fetchZekay(this.zekayAPI + `/events/${eventId}/participants/${participantId}`, {
      method: "DELETE",
    });
    await expectStatuses(r, 204);
  }

  async toggleEventOnline(eventId: number, online: boolean): Promise<void> {
    const r = await fetchZekay(this.zekayAPI + `/events/${eventId}/online`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_online: online }),
    });
    await expectStatuses(r, 204);
  }

  async uploadEventBackground(eventId: number, file: File): Promise<string> {
    const body = new FormData();
    body.append("File", file);
    const r = await fetchZekay(this.zekayAPI + `/events/${eventId}/background`, {
      method: "POST",
      body,
    });
    await expectStatuses(r, 200);
    const data = (await r.json()) as { background_url: string };
    return data.background_url;
  }
}
